import {
  moveChapterAfter,
  moveChapterBefore,
  moveLesson,
  moveLessonAfter,
  moveLessonBefore,
  type Course,
} from "@repo/app-types/entities/course";
import { isNotUndefined } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalCloseButton } from "@repo/ui/Modal/modalCloseButton";
import { modalHeader } from "@repo/ui/Modal/modalHeader";
import { IconMessageCircle } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import type { DragItem } from "react-aria";
import {
  Dialog,
  DialogTrigger,
  Header,
  Heading,
  useDragAndDrop,
  type Selection,
} from "react-aria-components";
import { getAxiosInstanceWithAuth } from "../../../../api/axiosConfig.js";
import type { VisibleInterviewSectionDefinition } from "../../../interviewSections/definitions/state.js";
import {
  InterviewSimulation,
  type ChatMessage,
} from "../interviewSimulation/interviewSimulation.js";
import {
  deserializeTreeItemKey,
  serializeTreeItemKey,
  type TreeItemKey,
} from "./courseTreeHelpers.js";

export function useCourseTreeSelection(props: {
  initialSelectedKey: TreeItemKey | undefined;
  course: Course | undefined;
  sectionsById: Map<string, VisibleInterviewSectionDefinition> | undefined;
}) {
  const { course, sectionsById } = props;

  const [selectedKey, setSelectedKey] = useState<TreeItemKey | undefined>(
    props.initialSelectedKey,
  );

  const selectedKeys = useMemo(
    () => (selectedKey ? [serializeTreeItemKey(selectedKey)] : []),
    [selectedKey],
  );

  const onSelectionChange = (selection: Selection) => {
    if (selection === "all") {
      return;
    }

    const selectedKeys = Array.from(selection).map(deserializeTreeItemKey);
    const onlySelectedKey =
      selectedKeys.length === 1 ? selectedKeys[0] : undefined;
    setSelectedKey(onlySelectedKey);
  };

  const selectedChapterId =
    selectedKey?.type === "chapter" ? selectedKey.id : undefined;
  const chapterNumbersById = useMemo(
    () =>
      Object.fromEntries(
        course?.chapters.map((chapter, index) => [chapter.id, index + 1]) ?? [],
      ),
    [course],
  );
  const selectedChapterNumber = selectedChapterId
    ? chapterNumbersById[selectedChapterId]
    : undefined;
  const selectedChapter = course?.chapters.find(
    (chapter) => chapter.id === selectedChapterId,
  );

  const selectedSectionId =
    selectedKey?.type === "lesson" ? selectedKey.sectionId : undefined;
  const lessonNumbersBySectionId = useMemo(
    () =>
      Object.fromEntries(
        course?.chapters.flatMap((chapter, chapterIndex) =>
          chapter.lessons.map(
            (lesson, lessonIndex) =>
              [
                lesson.interviewSectionDefinitionId,
                `${chapterIndex + 1}.${lessonIndex + 1}`,
              ] as const,
          ),
        ) ?? [],
      ),
    [course],
  );
  const selectedLessonNumber = selectedSectionId
    ? lessonNumbersBySectionId[selectedSectionId]
    : undefined;
  const selectedSection = selectedSectionId
    ? sectionsById?.get(selectedSectionId)
    : undefined;

  return {
    selectedKeys,
    onSelectionChange,
    selectedChapterNumber,
    selectedChapter,
    selectedLessonNumber,
    selectedSection,
    setSelectedKey,
  };
}

export function useCourseTreeDragAndDrop(
  course: Course,
  setChangedCourse: (newCourse: Course) => void,
) {
  return useDragAndDrop({
    acceptedDragTypes: [
      "application/vnd.alpharun.chapter",
      "application/vnd.alpharun.lesson",
    ],

    getItems: (keys) =>
      Array.from(keys)
        .map((key): DragItem | undefined => {
          const treeItemKey = deserializeTreeItemKey(key);
          switch (treeItemKey?.type) {
            case "chapter":
              return {
                "application/vnd.alpharun.chapter": treeItemKey.id,
              };

            case "lesson":
              return {
                "application/vnd.alpharun.lesson": treeItemKey.sectionId,
              };

            default:
              return undefined;
          }
        })
        .filter(isNotUndefined),

    shouldAcceptItemDrop: (target, types) => {
      const treeItemKey = deserializeTreeItemKey(target.key);
      switch (treeItemKey?.type) {
        case "chapter":
          switch (target.dropPosition) {
            case "on":
              return types.has("application/vnd.alpharun.lesson");

            case "before":
            case "after":
              return types.has("application/vnd.alpharun.chapter");

            default:
              return false;
          }

        case "lesson":
          switch (target.dropPosition) {
            case "before":
            case "after":
              return types.has("application/vnd.alpharun.lesson");

            default:
              return false;
          }

        default:
          return false;
      }
    },

    onMove: ({ target, keys }) => {
      const targetTreeItemKey = deserializeTreeItemKey(target.key);
      const treeItemKey = Array.from(keys).map(deserializeTreeItemKey).at(0);

      if (!targetTreeItemKey || !treeItemKey) {
        return;
      }

      if (
        treeItemKey.type === "chapter" &&
        target.dropPosition === "before" &&
        targetTreeItemKey.type === "chapter"
      ) {
        setChangedCourse(
          moveChapterBefore(course, treeItemKey.id, targetTreeItemKey.id),
        );
      } else if (
        treeItemKey.type === "chapter" &&
        target.dropPosition === "after" &&
        targetTreeItemKey.type === "chapter"
      ) {
        setChangedCourse(
          moveChapterAfter(course, treeItemKey.id, targetTreeItemKey.id),
        );
      } else if (
        treeItemKey.type === "lesson" &&
        (target.dropPosition === "on" || target.dropPosition === "after") &&
        targetTreeItemKey.type === "chapter"
      ) {
        setChangedCourse(
          moveLesson(course, treeItemKey.sectionId, targetTreeItemKey.id),
        );
      } else if (
        treeItemKey.type === "lesson" &&
        target.dropPosition === "before" &&
        targetTreeItemKey.type === "lesson"
      ) {
        setChangedCourse(
          moveLessonBefore(
            course,
            treeItemKey.sectionId,
            targetTreeItemKey.sectionId,
          ),
        );
      } else if (
        treeItemKey.type === "lesson" &&
        target.dropPosition === "after" &&
        targetTreeItemKey.type === "lesson"
      ) {
        setChangedCourse(
          moveLessonAfter(
            course,
            treeItemKey.sectionId,
            targetTreeItemKey.sectionId,
          ),
        );
      } else if (
        treeItemKey.type === "lesson" &&
        target.dropPosition === "before" &&
        targetTreeItemKey.type === "new-lesson"
      ) {
        setChangedCourse(
          moveLesson(
            course,
            treeItemKey.sectionId,
            targetTreeItemKey.chapterId,
          ),
        );
      }
    },
  });
}

export function renderSectionPreviewTrigger(
  projectId: string,
  section: VisibleInterviewSectionDefinition | undefined,
) {
  const interviewSectionDefinitionId = section?.id.startsWith("client:")
    ? undefined
    : section?.id;

  const getNextMessage = async (
    messages: readonly ChatMessage[],
  ): Promise<{ messages: ChatMessage[] }> => {
    const axios = await getAxiosInstanceWithAuth();
    const response = await axios.post(
      `/projects/${encodeURIComponent(projectId)}/simulate-conversation`,
      {
        messages,
        interviewSectionDefinitionId,
        interviewSectionSettings: section?.settings,
      },
    );
    return response.data;
  };

  return (
    <DialogTrigger>
      <Button
        variant={ButtonVariant.Secondary}
        icon={<IconMessageCircle className="size-4" />}
        className="disabled:invisible"
        isDisabled={!section}
      >
        Preview
      </Button>

      <CenteredModal isDismissable={true}>
        {section ? (
          <Dialog className="flex h-[80dvh] w-dvw max-w-lg flex-col">
            <Header
              className={modalHeader().header({
                className: "p-4 sm:p-6",
              })}
            >
              <Heading slot="title" className={modalHeader().title()}>
                Lesson preview
              </Heading>
              <ModalCloseButton />
            </Header>

            <InterviewSimulation getNextMessage={getNextMessage} />
          </Dialog>
        ) : null}
      </CenteredModal>
    </DialogTrigger>
  );
}
